import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { CalloutHero } from 'marketplace-discovery-studio-lib/components/CalloutHero';
import { CalloutCard } from 'marketplace-discovery-studio-lib/components/CalloutCard';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getMarketplaceInteractionEvent } from 'marketplace-ui-tracking/events/marketplace';
import { Subscreens } from '../../constants/usage';
import { PageCalloutBannerCard } from './PageCalloutBannerCard';
import { HeroButton } from './HeroButton';
import { FindPartnerMatch } from './FindPartnerMatch';
import { useIsOpenState } from '../hooks/useIsOpenState';
import { getCalloutCta, isCalloutPlacement, useCurrentPageId, usePagePlacements } from '../hooks/usePagePlacements';
import { resolveCalloutButtonUrl } from '../utils/resolveCalloutButtonUrl';

/**
 * Top-row callouts. Layout follows Compose published view: occupancy-driven.
 * - Only TOP_HERO → full-width hero
 * - TOP_HERO + TOP_CARD → 2/3 + 1/3 split
 * Empty slots are skipped; nothing depends on TOP_CARD being present.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function PageCallouts({
  showBanners = true
}) {
  const pageId = useCurrentPageId();
  const {
    placementsBySection,
    loading
  } = usePagePlacements(pageId);
  const {
    open,
    isOpen,
    close
  } = useIsOpenState();
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const {
    layout
  } = useResponsiveProps({
    layout: {
      xs: 'mobile',
      md: 'tablet',
      lg: 'desktop'
    }
  });
  const topRowLayout = layout !== null && layout !== void 0 ? layout : 'desktop';
  if (loading || !pageId) {
    return null;
  }
  const topHeroPlacement = placementsBySection['TOP_HERO'];
  const topCardPlacement = placementsBySection['TOP_CARD'];
  const topHero = topHeroPlacement && isCalloutPlacement(topHeroPlacement) ? topHeroPlacement : undefined;
  const topCard = topCardPlacement && isCalloutPlacement(topCardPlacement) ? topCardPlacement : undefined;
  const banners = showBanners ? ['BANNER_1', 'BANNER_2'].map(section => placementsBySection[section]).filter(placement => Boolean(placement) && isCalloutPlacement(placement)) : [];
  const hasTopRow = Boolean(topHero || topCard);
  const isSplitTopRow = Boolean(topHero && topCard) && topRowLayout === 'desktop';
  if (!hasTopRow && banners.length === 0) {
    return null;
  }
  const heroCta = topHero ? getCalloutCta(topHero) : undefined;
  const hasSecondaryButton = Boolean((heroCta === null || heroCta === void 0 ? void 0 : heroCta.secondaryButtonText) && (heroCta === null || heroCta === void 0 ? void 0 : heroCta.secondaryButtonUrl));
  const cardCta = topCard ? getCalloutCta(topCard) : undefined;
  const trackHeroClick = () => {
    trackMarketplaceEvent(getMarketplaceInteractionEvent({
      action: 'hero button clicked',
      subscreen: Subscreens.HERO_SECTION
    }));
    if (heroCta !== null && heroCta !== void 0 && heroCta.openFindPartnerMatch) {
      open();
    }
  };
  const renderPrimaryCallout = () => {
    if (!topHero) {
      return null;
    }
    const heroButtonUrl = resolveCalloutButtonUrl(topHero.calloutContent);

    // Mobile (and tablet without a desktop split): card layout is more compact.
    if (topRowLayout !== 'desktop') {
      return /*#__PURE__*/_jsx(CalloutCard, {
        title: topHero.calloutContent.title,
        buttonText: topHero.calloutContent.buttonText,
        buttonUrl: heroButtonUrl,
        imageUrl: topHero.calloutContent.imageUrl,
        imageAlt: topHero.calloutContent.title,
        videoId: topHero.calloutContent.videoId,
        openCopilot: heroCta === null || heroCta === void 0 ? void 0 : heroCta.openCopilot,
        onClick: trackHeroClick,
        CtaButton: HeroButton
      });
    }
    return /*#__PURE__*/_jsx(CalloutHero, {
      title: topHero.calloutContent.title,
      description: topHero.calloutContent.description,
      buttonText: topHero.calloutContent.buttonText,
      buttonUrl: heroButtonUrl,
      buttonUse: hasSecondaryButton ? 'primary' : 'secondary',
      secondaryButtonText: heroCta === null || heroCta === void 0 ? void 0 : heroCta.secondaryButtonText,
      secondaryButtonUrl: heroCta === null || heroCta === void 0 ? void 0 : heroCta.secondaryButtonUrl,
      openCopilot: heroCta === null || heroCta === void 0 ? void 0 : heroCta.openCopilot,
      onClick: trackHeroClick,
      imageUrl: topHero.calloutContent.imageUrl,
      videoId: topHero.calloutContent.videoId,
      CtaButton: HeroButton
    });
  };
  const renderSecondaryCallout = () => {
    if (!topCard) {
      return null;
    }
    if (topRowLayout === 'desktop') {
      return /*#__PURE__*/_jsx(Flex, {
        flex: "1",
        flexDirection: "column",
        minBlockSize: "0",
        minInlineSize: "0",
        children: /*#__PURE__*/_jsx(CalloutCard, {
          title: topCard.calloutContent.title,
          buttonText: topCard.calloutContent.buttonText,
          buttonUrl: resolveCalloutButtonUrl(topCard.calloutContent),
          imageUrl: topCard.calloutContent.imageUrl,
          imageAlt: topCard.calloutContent.title,
          videoId: topCard.calloutContent.videoId,
          openCopilot: cardCta === null || cardCta === void 0 ? void 0 : cardCta.openCopilot,
          CtaButton: HeroButton
        })
      });
    }
    return /*#__PURE__*/_jsx(PageCalloutBannerCard, {
      placement: topCard
    });
  };
  return /*#__PURE__*/_jsxs(View, {
    marginBlockEnd: "600",
    children: [hasTopRow && /*#__PURE__*/_jsxs(Flex, {
      flexDirection: topRowLayout === 'desktop' ? 'row' : 'column',
      gap: "400",
      marginBlockEnd: banners.length > 0 ? '400' : '0',
      alignItems: "stretch",
      children: [topHero && /*#__PURE__*/_jsx(Flex, {
        flex: isSplitTopRow ? '2' : '1',
        flexDirection: "column",
        minBlockSize: "0",
        minInlineSize: "0",
        inlineSize: topRowLayout === 'desktop' ? undefined : '100%',
        children: renderPrimaryCallout()
      }), renderSecondaryCallout()]
    }), banners.length > 0 && /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "400",
      children: banners.map(banner => /*#__PURE__*/_jsx(PageCalloutBannerCard, {
        placement: banner
      }, banner.section))
    }), isOpen && /*#__PURE__*/_jsx(FindPartnerMatch, {
      onClose: close
    })]
  });
}